import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  AddCircleOutlineIcon,
  Button,
  Radio,
  Skeleton,
  Text,
  Tooltip,
  TrashIcon,
} from '@mercai/clever'


import { DECIMAL_PLACES_DEFAULT } from '@/constants'
import { formatCurrency } from '@/helpers'

import { SelectAdditionalSupplierProductsProps } from './select-additional-supplier-products.type'
import { SectionHeader } from '../../../../components/section-header'
import styles from './select-additional-supplier-products.module.css'

export const SelectAdditionalSupplierProducts = ({
  values,
  setValues,
  disabled,
  onShowAddAdditionalProducts,
  preview,
  isLoading,
  position,
  onChangeCurrency,
}: SelectAdditionalSupplierProductsProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenSupplierOrderManagementComponents')

  const onRemoveAdditionalProduct = (catalogProductUuid: string) => {
    setValues((prev) => ({
      ...prev,
      additionalProducts: prev?.additionalProducts?.filter(
        (product) => product.catalogProductUuid !== catalogProductUuid,
      ),
    }))
  }

  const Loading = () => {
    return (
      <div className={styles['info-row-content']}>
        <div className={styles['products-card-body']}>
          {[0, 1].map((i) => (
            <React.Fragment key={i}>
              <div className={styles['products-card-body-item']}>
                <Skeleton height="1rem" width="10rem" />
              </div>
              <div className={styles['products-card-body-item']}>
                <Skeleton height="1rem" width="5rem" />
              </div>
              <div className={styles['products-card-body-item']}>
                <Skeleton height="1rem" width="6rem" />
              </div>
              <div className={styles['products-card-body-item-total']}>
                <Skeleton height="1rem" width="7rem" />
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={disabled ? styles.disabled : ''}>
      <SectionHeader
        title={`${position}. ${t('componentSelectAdditionalSupplierProductsTitle')}`}
      >
        <div className={styles['info-block']}>
          <div className={styles['info-row-content']}>
            <Text className={styles['info-item']}>
              {t('componentSelectAdditionalSupplierProductsCurrencyLabel')}
            </Text>

            <Radio
              value={values?.currency === 'MXN'}
              onChange={() =>
                values?.currency !== 'MXN' ? onChangeCurrency('MXN') : null
              }
            >
              {t('componentSelectAdditionalSupplierProductsCurrencyMXNLabel')}
            </Radio>
            <Radio
              value={values?.currency === 'USD'}
              onChange={() =>
                values?.currency !== 'USD' ? onChangeCurrency('USD') : null
              }
            >
              {t('componentSelectAdditionalSupplierProductsCurrencyUSDLabel')}
            </Radio>
          </div>
          <div className={styles['sale-orders-header']}>
            <Button
              variant="ghost"
              className={styles['add-button']}
              onClick={onShowAddAdditionalProducts}
            >
              {t('componentSelectAdditionalSupplierProductsAddButton')}
              <AddCircleOutlineIcon />
            </Button>
          </div>
        </div>

        {isLoading && <Loading />}

        {!isLoading && (
          <div className={styles['info-row-content']}>
            <>
              {!preview?.additionalProducts?.length ||
                preview?.additionalProducts?.length === 0 ? (
                <>
                  <Text className={styles['info-item']}>
                    {t('componentSelectAdditionalSupplierProductsEmptyTitle')}
                  </Text>
                </>
              ) : (
                <div className={styles['products-card-body']}>
                  {preview?.additionalProducts?.map(
                    (
                      { cost, quantity, catalogProduct, total, category },
                      index,
                    ) => (
                      <React.Fragment key={index}>
                        <div className={styles['products-card-body-item']}>
                          <Tooltip
                            text={catalogProduct?.attributes?.title}
                            className={styles.tooltip}
                          >
                            <Text className={styles['product-title']}>
                              {catalogProduct?.attributes?.title}
                            </Text>
                          </Tooltip>

                          <Button
                            variant="secondary"
                            onClick={() =>
                              onRemoveAdditionalProduct(catalogProduct?.uuid)
                            }
                            disabled={disabled}
                            className={styles['delete-button']}
                          >
                            <TrashIcon />
                          </Button>
                        </div>

                        <div className={styles['products-card-body-item']}>
                          <Text variant="text-small">
                            {formatCurrency(
                              +cost,
                              'es-MX',
                              values?.currency || 'MXN',
                              DECIMAL_PLACES_DEFAULT,
                            )}
                          </Text>
                        </div>

                        <div className={styles['products-card-body-item']}>
                          <Text variant="text-small">{quantity} kg</Text>
                        </div>

                        <div className={styles['products-card-body-item']}>
                          <Text variant="text-small">
                            {category
                              ? tEnums(`product_quality_category_${category}`)
                              : '-'}
                          </Text>
                        </div>

                        <Text
                          variant="title-base"
                          className={styles['products-card-body-item-total']}
                        >
                          {formatCurrency(
                            +total,
                            'es-MX',
                            values?.currency || 'MXN',
                            DECIMAL_PLACES_DEFAULT,
                          )}
                        </Text>
                      </React.Fragment>
                    ),
                  )}
                </div>
              )}
            </>
          </div>
        )}
      </SectionHeader>
    </div>
  )
}
